import React, { useState, useCallback, useRef, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Input,
  Field,
  Card,
  Flex,
  Icon,
  Tooltip,
  createToaster,
  Toaster
} from '@chakra-ui/react'
import { FiArrowRight, FiUpload, FiCheck } from 'react-icons/fi'
import { OnboardingLayout } from '../../components/onboarding/OnboardingLayout'
import { useOnboarding } from '../../contexts/OnboardingContext'
import { ImproveableTextarea } from '../../components/onboarding/ImproveableTextarea'
import { InfoTooltip } from '../../components/InfoTooltip'
import type { BasicInfo } from '../../types/basic-setup'
import { getPhotoStatusMessage, validatePhotoFile } from '../../types/basic-setup'

const SERVICE_DELIVERY_SUGGESTIONS = ['1:1 sessions', 'Program', 'Group', 'Other']
const PRODUCT_DELIVERY_SUGGESTIONS = ['One-time purchase', 'Subscription', 'Bundle', 'Other']
const TIMEFRAME_SUGGESTIONS = ['2 weeks', '4 weeks', '6 weeks', '8 weeks', '12 weeks', 'Ongoing']

export const BasicSetupPage: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const {
    onboardingMode,
    basic_info,
    updateBasicInfo,
    saveBasicSetup,
    isSaving,
    error,
    uploadPhoto
  } = useOnboarding()

  const isProduct = onboardingMode === 'product'
  const deliverySuggestions = isProduct ? PRODUCT_DELIVERY_SUGGESTIONS : SERVICE_DELIVERY_SUGGESTIONS
  
  // Get path prefix from current route
  const getPathPrefix = () => {
    if (location.pathname.startsWith('/for-products/onboarding')) return '/for-products/onboarding'
    if (location.pathname.startsWith('/for-services/onboarding')) return '/for-services/onboarding'
    return '/for-services/onboarding' // Default fallback
  }
  const pathPrefix = getPathPrefix()

  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false)
  const [uploadProgress, setUploadProgress] = useState<number | null>(null)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  // Create toaster instance
  const toaster = useMemo(() => createToaster({
    placement: 'top-end',
    pauseOnPageIdle: true,
  }), [])

  // Auto-save on field change with debounce
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const saveSequenceRef = useRef<number>(0)
  const isTypingRef = useRef<boolean>(false)

  const handleFieldChange = useCallback((field: keyof BasicInfo, value: string) => {
    // Mark as typing to prevent save conflicts
    isTypingRef.current = true

    // Update local state immediately
    updateBasicInfo({ [field]: value })

    // Debounce auto-save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current)
    }

    saveTimeoutRef.current = setTimeout(async () => {
      const currentSequence = ++saveSequenceRef.current
      isTypingRef.current = false

      try {
        await saveBasicSetup()
      } catch (err) {
        // Only show toast for errors if this is still the latest save
        if (currentSequence === saveSequenceRef.current) {
          const error = err as Error
          // Don't show toast for connection errors - they're expected during development
          if (!error?.message?.includes('Cannot connect to server') &&
              !error?.message?.includes('Request timed out')) {
            toaster.create({
              title: 'Auto-save failed',
              description: error.message || 'Could not save your changes.',
              type: 'error',
              duration: 5000
            })
          }
        }
      }
    }, 1500) // Unified debounce time across all onboarding steps
  }, [updateBasicInfo, saveBasicSetup])

  const handleNext = async () => {
    // Double-check validation before proceeding
    if (!isFormValid()) {
      toaster.create({
        title: 'Missing required fields',
        description: 'Please fill in all required fields before continuing.',
        type: 'warning',
        duration: 5000
      })
      return
    }
    try {
      await saveBasicSetup()
      navigate(`${pathPrefix}/preview`)
    } catch (err) {
      const error = err as Error
      // Only show toast for non-connection errors
      if (!error?.message?.includes('Cannot connect to server') &&
          !error?.message?.includes('Request timed out')) {
        toaster.create({
          title: 'Save failed',
          description: error.message || 'Could not save your setup. Please try again.',
          type: 'error',
          duration: 7000
        })
      }
    }
  }

  const isFormValid = () => {
    if (!basic_info) return false
    return (
      Boolean(basic_info.name?.trim()) &&
      Boolean(basic_info.who_you_help?.trim()) &&
      Boolean(basic_info.offer_primary_outcome?.trim()) &&
      Boolean(basic_info.photo_url)
    )
  }

  const handlePhotoButtonClick = () => {
    setUploadError(null)
    fileInputRef.current?.click()
  }

  const handlePhotoSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    // Validate file before uploading
    const validation = validatePhotoFile(file)
    if (!validation.valid) {
      setUploadError(validation.error || 'Invalid file')
      toaster.create({
        title: 'Invalid file',
        description: validation.error || 'Please select a valid image file.',
        type: 'error',
        duration: 5000
      })
      return
    }

    // Show warning if file is large (compression will handle it)
    if (validation.warning) {
      toaster.create({
        title: 'Large file detected',
        description: validation.warning,
        type: 'info',
        duration: 4000
      })
    }

    setUploadError(null)
    setUploadProgress(0)
    setIsUploadingPhoto(true)

    try {
      await uploadPhoto(file, (progress) => setUploadProgress(progress))
      toaster.create({
        title: 'Photo uploaded',
        description: 'Your photo has been uploaded successfully.',
        type: 'success',
        duration: 3000
      })
    } catch (err) {
      const error = err as Error
      const errorMessage = error?.message || 'Upload failed'
      setUploadError(errorMessage)
      toaster.create({
        title: 'Upload failed',
        description: errorMessage,
        type: 'error',
        duration: 7000
      })
    } finally {
      setIsUploadingPhoto(false)
      setUploadProgress(null)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  return (
    <OnboardingLayout
      currentStep={1}
      totalSteps={5}
      stepLabels={['Basic Setup', 'Preview', 'Landing page', 'Targeting', 'Select package']}
    >
      <Box userSelect="text" style={{ WebkitUserSelect: 'text', MozUserSelect: 'text', msUserSelect: 'text' }}>
        <VStack gap={8} align="stretch" maxW="3xl" mx="auto" px={{ base: 4, sm: 6, lg: 8 }} pb={24}>
        {/* Header */}
        <VStack gap={4} textAlign="center">
          <Heading size={{ base: '2xl', md: '3xl' }} color="gray.900">
            Make a quick intro
          </Heading>
          <Text fontSize="lg" color="gray.600" maxW="2xl">
            We’ll generate a story-based carousel ad from a few essentials. You can refine it later.
          </Text>
        </VStack>

        {/* Error Display */}
        {error && (
          <Box
            p={4}
            bg="red.50"
            borderWidth="1px"
            borderColor="red.200"
            borderRadius="lg"
            textAlign="center"
          >
            <Text fontSize="sm" fontWeight="medium" color="red.800">
              {error}
            </Text>
          </Box>
        )}

        {/* Form */}
        <Card.Root variant="elevated" borderRadius="2xl">
          <Card.Body p={{ base: 6, md: 10 }}>
            <VStack gap={7} align="stretch">
              {/* Name */}
              <Field.Root required>
                <HStack gap={1} mb={1}>
                  <Field.Label fontSize="sm" fontWeight="semibold" color="gray.800">
                    {isProduct ? 'Brand or product name' : 'Name'}
                  </Field.Label>
                  <Text as="span" color="red.500">*</Text>
                  <InfoTooltip
                    content={
                      isProduct
                        ? 'Your brand or product name as it should appear in the carousel.'
                        : 'Your name or business name as it should appear in the carousel.'
                    }
                    ariaLabel="Name help"
                  />
                </HStack>
                <Input
                  size="lg"
                  value={basic_info?.name || ''}
                  onChange={(e) => handleFieldChange('name', e.target.value)}
                  placeholder={isProduct ? 'e.g., CalmSleep Magnesium' : 'e.g., Dr. Jane Smith'}
                  borderRadius="lg"
                  autoComplete="off"
                  userSelect="text"
                  maxLength={100}
                  _hover={{ borderColor: 'gray.300' }}
                  _focus={{ borderColor: 'brand.500', boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)' }}
                />
                <Text fontSize="xs" color="gray.500" mt={1}>
                  {(basic_info?.name || '').length}/100 characters
                </Text>
              </Field.Root>

              {/* Who you help */}
              <Field.Root required>
                <HStack gap={1} mb={1}>
                  <Field.Label fontSize="sm" fontWeight="semibold" color="gray.800">
                    Who is this for?
                  </Field.Label>
                  <Text as="span" color="red.500">*</Text>
                  <InfoTooltip
                    content={
                      isProduct
                        ? "Describe your ideal buyer in plain language. This helps the carousel connect with the right people. We'll lightly polish wording for clarity in your preview. You can edit anytime."
                        : "Describe your ideal customer in plain language. This helps the carousel connect with the right people. We'll lightly polish wording for clarity in your preview. You can edit anytime."
                    }
                    ariaLabel="Who is this for help"
                  />
                </HStack>
                <ImproveableTextarea
                  value={basic_info?.who_you_help || ''}
                  onChange={(value) => handleFieldChange('who_you_help', value)}
                  context="who_you_help"
                  fieldType="ad_body"
                  onboardingMode={onboardingMode}
                  placeholder={isProduct ? 'e.g., Busy parents who want better sleep' : 'e.g., Busy parents who need energy to get through the week'}
                  rows={3}
                  size="lg"
                  borderRadius="lg"
                  resize="none"
                  autoComplete="off"
                  userSelect="text"
                  maxLength={200}
                  _hover={{ borderColor: 'gray.300' }}
                  _focus={{ borderColor: 'brand.500', boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)' }}
                />
              </Field.Root>

              {/* Primary outcome */}
              <Field.Root required>
                <HStack gap={1} mb={1}>
                  <Field.Label fontSize="sm" fontWeight="semibold" color="gray.800">
                    {isProduct ? 'What change does it help them achieve?' : 'What change do you help them achieve?'}
                  </Field.Label>
                  <Text as="span" color="red.500">*</Text>
                  <InfoTooltip
                    content={
                      isProduct
                        ? "The main transformation or outcome buyers want. Be specific and concrete. We'll lightly polish wording for clarity in your preview. You can edit anytime."
                        : "The main transformation or outcome clients want. Be specific and concrete. We'll lightly polish wording for clarity in your preview. You can edit anytime."
                    }
                    ariaLabel="Outcome help"
                  />
                </HStack>
                <ImproveableTextarea
                  value={basic_info?.offer_primary_outcome || ''}
                  onChange={(value) => handleFieldChange('offer_primary_outcome', value)}
                  context="offer_primary_outcome"
                  fieldType="ad_body"
                  onboardingMode={onboardingMode}
                  placeholder={isProduct ? 'e.g., Deeper sleep and calmer mornings' : 'e.g., Better sleep and steady energy'}
                  rows={3}
                  size="lg"
                  borderRadius="lg"
                  resize="none"
                  autoComplete="off"
                  userSelect="text"
                  maxLength={200}
                  _hover={{ borderColor: 'gray.300' }}
                  _focus={{ borderColor: 'brand.500', boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)' }}
                />
              </Field.Root>

              {/* Delivery + timeframe (optional) */}
              <Field.Root>
                <Field.Label fontSize="sm" fontWeight="semibold" color="gray.800">
                  How is it delivered? (optional)
                </Field.Label>
                <Flex flexWrap="wrap" gap={2} mt={1}>
                  {deliverySuggestions.map((v) => (
                    <Button
                      key={v}
                      size="sm"
                      variant={basic_info?.offer_delivery === v ? 'solid' : 'outline'}
                      colorPalette={basic_info?.offer_delivery === v ? 'brand' : 'gray'}
                      borderRadius="full"
                      onClick={() => handleFieldChange('offer_delivery', v)}
                    >
                      {v}
                    </Button>
                  ))}
                </Flex>
              </Field.Root>

              <Field.Root>
                <Field.Label fontSize="sm" fontWeight="semibold" color="gray.800">
                  Typical timeframe? (optional)
                </Field.Label>
                <Flex flexWrap="wrap" gap={2} mt={1}>
                  {TIMEFRAME_SUGGESTIONS.map((v) => (
                    <Button
                      key={v}
                      size="sm"
                      variant={basic_info?.offer_timeframe === v ? 'solid' : 'outline'}
                      colorPalette={basic_info?.offer_timeframe === v ? 'brand' : 'gray'}
                      borderRadius="full"
                      onClick={() => handleFieldChange('offer_timeframe', v)}
                    >
                      {v}
                    </Button>
                  ))}
                </Flex>
              </Field.Root>

              {/* Photo Upload */}
              <Field.Root required>
                <HStack gap={1} mb={1}>
                  <Field.Label fontSize="sm" fontWeight="semibold" color="gray.800">
                    {isProduct ? 'Add an image' : 'Add a photo'}
                  </Field.Label>
                  <Text as="span" color="red.500">*</Text>
                  <InfoTooltip
                    content={
                      isProduct
                        ? "A clear, real image is required. Use your product packshot or a founder image - it's a key trust signal."
                        : "A real photo is required - it's a key trust signal that helps people connect with you."
                    }
                    ariaLabel="Photo help"
                  />
                </HStack>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/heic,image/heif"
                  capture="environment"
                  style={{ display: 'none' }}
                  onChange={handlePhotoSelected}
                />
                <Button
                  w="full"
                  size="lg"
                  variant="outline"
                  borderStyle="dashed"
                  borderRadius="lg"
                  onClick={handlePhotoButtonClick}
                  loading={isUploadingPhoto}
                  loadingText="Uploading..."
                  _hover={{ bg: 'brand.50', borderColor: 'brand.300', color: 'brand.700' }}
                >
                  <HStack gap={2}>
                    <Icon as={basic_info?.photo_url ? FiCheck : FiUpload} color={basic_info?.photo_url ? 'brand.600' : undefined} />
                    <Text fontWeight="medium">
                      {basic_info?.photo_url ? 'Replace photo' : 'Upload a photo'}
                    </Text>
                  </HStack>
                </Button>
                <Text fontSize="xs" color="gray.500" mt={2}>
                  {isProduct
                    ? "A clear image is required - it's a key trust signal."
                    : "A real photo is required - it's a key trust signal."}
                </Text>
                {uploadProgress !== null && (
                  <Text fontSize="xs" color="gray.600" mt={1}>
                    Uploading... {Math.round(uploadProgress)}%
                  </Text>
                )}
                {basic_info?.photo_url && (
                  <Text fontSize="xs" color="green.700" mt={1}>
                    {getPhotoStatusMessage(basic_info.photo_status)}
                  </Text>
                )}
                {uploadError && (
                  <Text fontSize="xs" color="red.600" mt={1}>
                    {uploadError}
                  </Text>
                )}
              </Field.Root>

              {/* Next Button */}
              <Box borderTopWidth="1px" borderColor="gray.100" pt={6} mt={2} mb={4}>
                <Flex justify="flex-end" align="center">
                  <Button
                    size="lg"
                    colorPalette="brand"
                    onClick={handleNext}
                    disabled={!isFormValid() || isSaving}
                    loading={isSaving}
                    loadingText="Saving..."
                    _disabled={{ opacity: 0.6, cursor: 'not-allowed' }}
                  >
                    <HStack gap={2}>
                      <Text>Continue to preview</Text>
                      <Icon as={FiArrowRight} />
                    </HStack>
                  </Button>
                </Flex>
              </Box>
            </VStack>
          </Card.Body>
        </Card.Root>
      </VStack>
      </Box>
      <Toaster toaster={toaster}>{() => null}</Toaster>
    </OnboardingLayout>
  )
}